import { useEffect, useRef, useState } from "react";
import { api, type Message, type Repeat } from "../api";
import { upsertTask, useStore } from "../store";
import { localToday, parseTask, plain } from "../text";
import { dmPartner, toast, useMe } from "./ui";

/** "+ Task" on a message: a small form next to it, with a guess at who and what. */
export function MakeTask({ m, anchor, onClose }: { m: Message; anchor: HTMLElement; onClose: () => void }) {
  const me = useMe();
  const users = useStore((s) => s.users);
  const room = useStore((s) => s.rooms[m.roomId]);
  const people = Object.values(users).filter((u) => u.active);
  const guess = parseTask(plain(m.body), people, null);
  const fallback = m.userId !== me.id ? m.userId : dmPartner(room, me.id) ?? people.find((u) => u.id !== me.id)?.id ?? me.id;

  const [title, setTitle] = useState(guess.title.slice(0, 120));
  const [owner, setOwner] = useState<number>(guess.ownerId ?? fallback);
  const [due, setDue] = useState(guess.due ?? "");
  const [repeat, setRepeat] = useState<Repeat | "">(guess.repeat ?? "");
  const [busy, setBusy] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  const r = anchor.getBoundingClientRect();
  const top = Math.min(r.bottom + 6, innerHeight - 330), left = Math.max(16, Math.min(r.left, innerWidth - 360));

  useEffect(() => {
    const k = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    const c = (e: MouseEvent) => !box.current?.contains(e.target as Node) && onClose();
    addEventListener("keydown", k); setTimeout(() => addEventListener("mousedown", c));
    return () => { removeEventListener("keydown", k); removeEventListener("mousedown", c); };
  }, [onClose]);

  const give = async () => {
    if (!title.trim()) return;
    setBusy(true);
    try {
      const t = await api.newTask({ title, ownerId: owner, due: due || null, repeat: repeat || null, sourceMessageId: m.id });
      upsertTask(t);
      toast(`Task for ${users[owner]?.name} · in Tasks and on the right`);
      onClose();
    } catch (e: any) { toast(e.message || "Couldn't make the task"); setBusy(false); }
  };

  return (
    <div className="pop" ref={box} style={{ position: "fixed", top, left }}>
      <div className="f"><label>Task</label><input type="text" autoFocus value={title} onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => e.key === "Enter" && give()} /></div>
      <div className="f"><label>For</label>
        <select value={owner} onChange={(e) => setOwner(Number(e.target.value))}>
          {people.map((u) => <option key={u.id} value={u.id}>{u.id === me.id ? `${u.name} (you)` : u.name}</option>)}
        </select>
      </div>
      <div className="two">
        <div className="f"><label>Due (optional)</label><input type="date" min={localToday()} value={due} onChange={(e) => setDue(e.target.value)} /></div>
        <div className="f"><label>Repeats</label>
          <select value={repeat} onChange={(e) => setRepeat(e.target.value as Repeat | "")}>
            <option value="">No</option><option value="daily">Every day</option><option value="weekdays">Every weekday</option><option value="weekly">Every week</option><option value="monthly">Every month</option>
          </select>
        </div>
      </div>
      <div className="acts"><button className="btn" onClick={onClose}>Cancel</button><button className="btn pri" disabled={busy || !title.trim()} onClick={give}>Give task</button></div>
    </div>
  );
}
